/* Copyright (c) 2024-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, take, tap, finalize } from 'rxjs/operators';
import { ConfigStoreService } from 'libraries/config-store/src/lib/config-store.service';
import { AppInfo } from 'libraries/config-store/src/lib/config-store.types';
import { DistributedTracingUtilService } from 'distributed-tracing/lib/services/distributedtracing-util.service';
import {
    SpanNames,
    SpanTagNames,
    ErrorMessageNames
} from 'distributed-tracing/lib/constants/distributedtracing_constants';
import { OpenTelemetrySpan } from 'distributed-tracing/lib/distributed-tracing';
import { Logger, LoggingService } from '../../logging/logging.service';
import { ApplicationName, LoginHintQueryKey, SsoLoginInfoEntry } from '../constants/constants';

@Injectable({
    providedIn: 'root'
})
export class LoginHintService {
    private logger: Logger;
    // store recently updated or read entry
    private cachedSsoLoginInfo: SsoLoginInfoEntry | null = null;
    private browserSsoLoginHint: SsoLoginInfoEntry = null;
    // flag to track if the current login hint update is successful for logged in user
    private currentloginSilentLoginInfoUpdateStatus: boolean = false;

    constructor(
        private configStore: ConfigStoreService,
        private loggingService: LoggingService,
        private router: Router,
        private distributedTracingUtilService: DistributedTracingUtilService
    ) {
        this.logger = this.loggingService.getLogger('silentlogin/loginhint-service');
    }

    public isEmptySilentLoginInfo(sil: SsoLoginInfoEntry): boolean {
        return (
            !sil ||
            !sil.idp_id ||
            !sil.login_hint ||
            !sil.applicationName ||
            sil.idp_id.trim() === '' ||
            sil.login_hint.trim() === '' ||
            sil.applicationName.trim() === ''
        );
    }

    private returnNullIfEmpty<T>(source: Observable<T>): Observable<T | null> {
        return source.pipe(
            map(value => {
                if (this.isEmptySilentLoginInfo(value as SsoLoginInfoEntry)) {
                    return null;
                }
                return value;
            })
        );
    }

    updateSsoLoginInfoEntry(
        isUserAuthenticated: boolean,
        idp_id: string,
        login_hint: string,
        applicationName: ApplicationName
    ): Observable<boolean> {
        const newSsoEntry: SsoLoginInfoEntry = {
            idp_id,
            login_hint,
            applicationName
        };
        if (isUserAuthenticated && !this.isEmptySilentLoginInfo(newSsoEntry)) {
            this.currentloginSilentLoginInfoUpdateStatus = true;
            return this.updateSsoLoginInfoEntryInternal(newSsoEntry);
        } else {
            return this.cleanUpSsoLoginInfo(applicationName);
        }
    }

    private updateSsoLoginInfoEntryInternal(newSsoLoginInfo: SsoLoginInfoEntry): Observable<boolean> {
        const span = this._startSpanAndSetDefaults(SpanNames.UpdateLoginHint);

        return this.getSsoLoginInfo().pipe(
            mergeMap(latestLoginInfo => {
                // Check if the new user information is different from the latest cached value
                if (
                    !latestLoginInfo ||
                    latestLoginInfo.applicationName !== newSsoLoginInfo?.applicationName ||
                    latestLoginInfo.idp_id !== newSsoLoginInfo?.idp_id ||
                    latestLoginInfo.login_hint !== newSsoLoginInfo?.login_hint
                ) {
                    return this.setSsoLoginInfo(newSsoLoginInfo);
                } else {
                    this.logger.info('No changes detected in SSO login info.');
                    // If the information is the same, do not update and return false
                    return of(false);
                }
            }),
            catchError(error => {
                this.logger.error('Error updating SsoLoginInfoEntry', error);
                this._traceError(span, error, ErrorMessageNames.UpdateSsoLoginInfoEntryError);
                return of(false); // Return false in case of an error
            }),
            finalize(() => {
                // Ensure span is closed regardless of success/error
                this.distributedTracingUtilService.closeSpan(span);
            })
        );
    }

    mapAppInfoToSsoLoginInfoEntry(appInfo: AppInfo): SsoLoginInfoEntry {
        return {
            idp_id: appInfo?.idpId,
            login_hint: appInfo?.loginHint,
            applicationName: appInfo?.applicationName as ApplicationName
        };
    }
    getSsoLoginInfo(): Observable<SsoLoginInfoEntry> {
        const span = this._startSpanAndSetDefaults(SpanNames.LoginHintGetSsoInfo);
        const browserSsoLoginHint = this.extractAndClearLoginHintQueryParams();

        if (browserSsoLoginHint && !this.isEmptySilentLoginInfo(browserSsoLoginHint)) {
            this.browserSsoLoginHint = browserSsoLoginHint;
            this.distributedTracingUtilService.closeSpan(span);
            return of(this.browserSsoLoginHint);
        }

        if (this.cachedSsoLoginInfo && !this.isEmptySilentLoginInfo(this.cachedSsoLoginInfo)) {
            this.distributedTracingUtilService.closeSpan(span);
            return of(this.cachedSsoLoginInfo);
        } else {
            return this.configStore.readAppInfoFromPersistence().pipe(
                map((appInfoList: AppInfo[] = []) => {
                    // Find the first valid SsoLoginInfoEntry
                    const validAppInfoEntry = appInfoList.find(appInfo => {
                        const ssoLoginInfoEntry = this.mapAppInfoToSsoLoginInfoEntry(appInfo);
                        return !this.isEmptySilentLoginInfo(ssoLoginInfoEntry);
                    });
                    // Return the valid entry or null if none found
                    return validAppInfoEntry ? this.mapAppInfoToSsoLoginInfoEntry(validAppInfoEntry) : null;
                }),
                this.returnNullIfEmpty.bind(this),
                tap(result => (this.cachedSsoLoginInfo = result)),
                catchError(error => {
                    this.logger.error('Error occurred while reading the loginids file -', error);
                    this._traceError(span, error, ErrorMessageNames.GetSsoLoginInfoError);
                    return of(null);
                }),
                finalize(() => {
                    this.distributedTracingUtilService.closeSpan(span);
                })
            );
        }
    }

    private extractAndClearLoginHintQueryParams(): SsoLoginInfoEntry {
        const url = new URL(window.location.href);
        const searchParams = url.searchParams;
        let browserSsoLoginHint: SsoLoginInfoEntry = null;

        if (searchParams.has(LoginHintQueryKey.LoginHint) && searchParams.has(LoginHintQueryKey.IdpId)) {
            const loginHint = searchParams.get(LoginHintQueryKey.LoginHint);
            const idpId = searchParams.get(LoginHintQueryKey.IdpId);

            if (loginHint && loginHint.trim() !== '' && idpId && idpId.trim() !== '') {
                const decodedLoginHint = decodeURIComponent(loginHint);
                const decodedIdpId = decodeURIComponent(idpId);

                browserSsoLoginHint = {
                    login_hint: decodedLoginHint,
                    idp_id: decodedIdpId,
                    applicationName: ApplicationName.GFN_WEB
                };
            } else {
                this.logger.error(
                    'Invalid login hint or idpId from query param. Both values must be provided and not empty.'
                );
            }

            // listen to router events NavigationEnd to clean up query params for login_hint and idp_id
            this.router.events
                .pipe(
                    filter(evt => evt instanceof NavigationEnd),
                    take(1)
                )
                .subscribe((event: NavigationEnd) => {
                    this.logger.info(`Cleaning up login hint from: ${event.url}`);
                    searchParams.delete(LoginHintQueryKey.LoginHint);
                    searchParams.delete(LoginHintQueryKey.IdpId);
                    window.history.replaceState({}, '', url);
                });
        }

        return browserSsoLoginHint;
    }

    private setSsoLoginInfo(ssoLoginInfoEntry: SsoLoginInfoEntry): Observable<boolean> {
        return this.configStore
            .writeAppInfoToPersistence({
                applicationName: ssoLoginInfoEntry.applicationName,
                idpId: ssoLoginInfoEntry?.idp_id,
                loginHint: ssoLoginInfoEntry?.login_hint
            })
            .pipe(
                tap((success: boolean) => {
                    if (success) {
                        // Update the cache with the written information
                        this.cachedSsoLoginInfo = ssoLoginInfoEntry;
                    }
                }),
                catchError(error => {
                    this.logger.error('Error occurred in writeConfigFile:', error);
                    return of(false);
                })
            );
    }

    private cleanUpSsoLoginInfo(appName: ApplicationName): Observable<boolean> {
        if (!this.isEmptySilentLoginInfo(this.cachedSsoLoginInfo)) {
            const cachedAppName = this.cachedSsoLoginInfo?.applicationName;
            this.cachedSsoLoginInfo = null;
            if (appName === cachedAppName && this.currentloginSilentLoginInfoUpdateStatus) {
                this.currentloginSilentLoginInfoUpdateStatus = false;
                return this.setSsoLoginInfo({ applicationName: appName, idp_id: '', login_hint: '' });
            }
        }

        this.browserSsoLoginHint = null;
        return of(true);
    }

    private _traceError(span: OpenTelemetrySpan, error: any, errorType: string): void {
        this.distributedTracingUtilService.setSpanAttribute(span, SpanTagNames.Error, true);
        this.distributedTracingUtilService.setSpanAttribute(span, SpanTagNames.ErrorType, errorType);
        this.distributedTracingUtilService.setSpanAttribute(
            span,
            SpanTagNames.ErrorDetails,
            error?.message || error?.toString() || `Unknown error in ${errorType}`
        );
    }

    private _startSpanAndSetDefaults(spanName: SpanNames): OpenTelemetrySpan {
        const span = this.distributedTracingUtilService.startSpan(spanName);
        this.distributedTracingUtilService.setSpanAttribute(span, SpanTagNames.Error, false);
        return span;
    }
}
